import React, { useState, useEffect } from 'react';
import { adminAPI, supportAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import Navbar from '../../components/Navbar';
import CompetitorCostPanel from '../../components/CompetitorCostPanel';
import { formatDateTimeIST } from '../../utils/timezone';

// ---- Full order-detail rendering (recursive: shows EVERY field) ----
const IST_TZ = 'Asia/Kolkata';
function humanizeKey(key) {
  return key
    .replace(/Paisa$/, 'Paisa') // keep, then space-split below
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}
function isIsoDate(v) {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v);
}
function formatScalar(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (isIsoDate(v)) { try { return new Date(v).toLocaleString('en-IN', { timeZone: IST_TZ }); } catch (_) { return v; } }
  return String(v);
}
function DetailValue({ value, hide }) {
  if (value === null || value === undefined || typeof value !== 'object') {
    return <span style={{ color: 'var(--gray-800, #1f2937)', wordBreak: 'break-word' }}>{formatScalar(value)}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span style={{ color: 'var(--gray-400, #9ca3af)' }}>(none)</span>;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {value.map((item, i) => (
          <div key={i} style={{ borderLeft: '2px solid var(--gray-200, #e5e7eb)', paddingLeft: 10 }}>
            <div style={{ fontSize: 11, color: 'var(--gray-400, #9ca3af)', marginBottom: 2 }}>#{i + 1}</div>
            <DetailValue value={item} hide={hide} />
          </div>
        ))}
      </div>
    );
  }
  const keys = Object.keys(value).filter((k) => k !== '__v' && k !== 'password' && !(hide && hide(k)));
  if (keys.length === 0) return <span style={{ color: 'var(--gray-400, #9ca3af)' }}>—</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {keys.map((k) => {
        const v = value[k];
        const nested = v !== null && typeof v === 'object';
        return (
          <div key={k} style={{ display: 'flex', flexDirection: nested ? 'column' : 'row', gap: nested ? 2 : 8, alignItems: nested ? 'stretch' : 'baseline' }}>
            <div style={{ fontWeight: 600, color: 'var(--gray-600, #4b5563)', minWidth: 150, fontSize: 13 }}>{humanizeKey(k)}</div>
            <div style={{ flex: 1, fontSize: 13, paddingLeft: nested ? 10 : 0 }}><DetailValue value={v} hide={hide} /></div>
          </div>
        );
      })}
    </div>
  );
}

function AdminOrders() {
  const { user } = useAuth();
  // Support staff share this page but hit the /api/support/* endpoints (full
  // access); admins hit /api/admin/*. Same method names, so pick the namespace.
  const opsAPI = user?.role === 'support' ? supportAPI : adminAPI;
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('');
  const [orderType, setOrderType] = useState(''); // '' all | 'food' | 'delivery_service'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [updatingOrder, setUpdatingOrder] = useState(null);
  const [retriggeringOrder, setRetriggeringOrder] = useState(null);
  const [processingApproval, setProcessingApproval] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showRawFields, setShowRawFields] = useState(true); // show ₹-paisa & technical duplicates
  // Per-order Swiggy/Zomato price comparison (on-demand, admin only)
  const [compCost, setCompCost] = useState(null);
  const [compLoading, setCompLoading] = useState(false);
  const [compErr, setCompErr] = useState('');

  // Admin-only "Support Activity" audit: order-status changes made by support.
  const isAdmin = user?.role === 'admin';
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);

  const fetchAudit = async () => {
    setAuditLoading(true);
    try {
      const res = await adminAPI.getOrderStatusAudit({ limit: 100 });
      setAuditLogs(res.data?.data?.logs || []);
    } catch (e) {
      console.error('Failed to load support activity:', e);
    } finally {
      setAuditLoading(false);
    }
  };

  const toggleAudit = () => {
    const next = !showAudit;
    setShowAudit(next);
    if (next) fetchAudit();
  };

  const openOrderDetail = async (order) => {
    setDetailOrder({ ...order, __partial: true }); // show what we have immediately
    setDetailLoading(true);
    setCompCost(null); setCompErr(''); setCompLoading(false); // reset competitor panel for the new order
    try {
      const res = await opsAPI.getOrderDetail(order._id);
      setDetailOrder(res.data?.data?.order || order);
    } catch (e) {
      console.error('Failed to load order detail:', e);
      setDetailOrder(order); // fall back to the list copy
    } finally {
      setDetailLoading(false);
    }
  };

  // On-demand: what this order's items are priced at on Swiggy/Zomato (menu-price basis).
  const loadCompetitorCost = async (orderId) => {
    setCompLoading(true); setCompErr('');
    try {
      const res = await adminAPI.getOrderCompetitorCost(orderId);
      setCompCost(res.data?.data || null);
    } catch (e) {
      setCompErr(e.response?.data?.message || 'Failed to load competitor prices');
    } finally {
      setCompLoading(false);
    }
  };

  const orderStatuses = [
    'pending',
    'restaurant_confirmed',
    'preparing',
    'ready_for_pickup',
    'assigned_delivery',
    'picked_up',
    'on_the_way',
    'delivered',
    'cancelled'
  ];

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      setUpdatingOrder(orderId);
      await opsAPI.updateTestOrderStatus(orderId, { status: newStatus });
      // Refresh orders to get updated data
      await fetchOrders();
      alert(`Order status updated to ${newStatus.replace(/_/g, ' ')}${newStatus === 'delivered' ? '. Invoices generated!' : ''}`);
    } catch (error) {
      console.error('Failed to update order status:', error);
      alert('Failed to update order status: ' + (error.response?.data?.message || error.message));
    } finally {
      setUpdatingOrder(null);
    }
  };

  const handleRetriggerDelivery = async (orderId, orderNumber, status) => {
    if (!window.confirm(`(Re)trigger delivery assignment for Order #${orderNumber} [${status}]?\n\nThis will:\n• Clear the current delivery partner (if any)\n• Start a fresh 7+7+R delivery assignment flow\n• Notify nearby delivery partners`)) {
      return;
    }

    try {
      setRetriggeringOrder(orderId);
      const response = await opsAPI.retriggerDeliveryAssignment(orderId);
      await fetchOrders();

      const data = response.data.data;
      let message = `✅ Fresh delivery assignment triggered for Order #${orderNumber}`;
      if (data.previousPartner) {
        message += `\n\nPrevious partner removed: ${data.previousPartner.name}`;
      }
      message += `\n\nNotified ${data.notifiedPartners} delivery partners`;

      alert(message);
    } catch (error) {
      console.error('Failed to retrigger delivery:', error);
      alert('Failed to retrigger delivery: ' + (error.response?.data?.message || error.message));
    } finally {
      setRetriggeringOrder(null);
    }
  };

  const handleApproveOrder = async (orderId, orderNumber) => {
    if (!window.confirm(`Approve order #${orderNumber}? It will be sent to the restaurant.`)) return;
    setProcessingApproval(orderId);
    try {
      await opsAPI.approveOrder(orderId);
      await fetchOrders();
    } catch (error) {
      alert('Failed to approve: ' + (error.response?.data?.message || error.message));
    } finally {
      setProcessingApproval(null);
    }
  };

  const handleRejectOrder = async (orderId, orderNumber) => {
    const reason = window.prompt(`Reject order #${orderNumber}? Enter a reason (the customer will be notified):`, '');
    if (reason === null) return; // prompt cancelled
    setProcessingApproval(orderId);
    try {
      await opsAPI.rejectOrder(orderId, reason || 'Rejected by admin');
      await fetchOrders();
    } catch (error) {
      alert('Failed to reject: ' + (error.response?.data?.message || error.message));
    } finally {
      setProcessingApproval(null);
    }
  };

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, orderType, startDate, endDate]);

  const fetchOrders = async () => {
    try {
      const params = {};
      if (filter) params.status = filter;
      if (orderType) params.orderType = orderType;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      // A date filter with the default 50-row cap would hide most matches — raise
      // the limit (server clamps to 500) so the whole day/range is returned.
      if (startDate || endDate) params.limit = 500;
      const response = await opsAPI.getAllOrders(params);
      setOrders(response.data.data.orders);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'linear-gradient(135deg, #ffa500 0%, #ff8c00 100%)',
      restaurant_confirmed: 'linear-gradient(135deg, #4ecdc4 0%, #44b8b1 100%)',
      preparing: 'linear-gradient(135deg, #4ecdc4 0%, #44b8b1 100%)',
      ready_for_pickup: 'linear-gradient(135deg, #51cf66 0%, #40c057 100%)',
      assigned_delivery: 'linear-gradient(135deg, #51cf66 0%, #40c057 100%)',
      picked_up: 'linear-gradient(135deg, #51cf66 0%, #40c057 100%)',
      on_the_way: 'linear-gradient(135deg, #51cf66 0%, #40c057 100%)',
      delivered: 'linear-gradient(135deg, #51cf66 0%, #40c057 100%)',
      cancelled: 'linear-gradient(135deg, #ff6b6b 0%, #e85a5a 100%)'
    };
    return colors[status] || 'linear-gradient(135deg, #666 0%, #888 100%)';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: '⏳',
      restaurant_confirmed: '✅',
      preparing: '👨‍🍳',
      ready_for_pickup: '📦',
      assigned_delivery: '🚴',
      picked_up: '📦',
      on_the_way: '🛵',
      delivered: '✅',
      cancelled: '❌'
    };
    return icons[status] || '📋';
  };

  if (loading) return <div className="loading">🔄 Loading orders...</div>;

  return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }} className="fade-in">
          <h1 style={{
            fontSize: 'var(--font-size-4xl)',
            fontWeight: '800',
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: 'var(--space-sm)'
          }}>
            📦 All Orders
          </h1>
          <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-lg)' }}>
            Monitor all platform orders • Auto-refreshes every 15 seconds
          </p>
        </div>

        {/* Support Activity (admin only): order-status changes made by support */}
        {isAdmin && (
          <div className="card fade-in" style={{ marginBottom: 'var(--space-xl)', borderLeft: '4px solid #f59e0b' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, color: 'var(--gray-900)' }}>🛡️ Support Activity</span>
              <span style={{ fontSize: 13, color: 'var(--gray-600)' }}>
                Order-status changes made by the support team
              </span>
              <button
                onClick={toggleAudit}
                className="btn"
                style={{ marginLeft: 'auto', padding: '6px 14px', background: showAudit ? '#e5e7eb' : '#f59e0b', color: showAudit ? '#374151' : '#fff', fontSize: 13 }}
              >
                {showAudit ? 'Hide' : 'View log'}
              </button>
              {showAudit && (
                <button onClick={fetchAudit} className="btn" style={{ padding: '6px 12px', background: '#3b82f6', color: '#fff', fontSize: 13 }}>
                  ↻ Refresh
                </button>
              )}
            </div>

            {showAudit && (
              <div style={{ marginTop: 14 }}>
                {auditLoading ? (
                  <div style={{ color: 'var(--gray-600)', fontSize: 13 }}>Loading…</div>
                ) : auditLogs.length === 0 ? (
                  <div style={{ color: 'var(--gray-600)', fontSize: 13 }}>No support status changes recorded yet.</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee', color: '#555' }}>
                          <th style={{ padding: '6px 10px' }}>When</th>
                          <th style={{ padding: '6px 10px' }}>Support member</th>
                          <th style={{ padding: '6px 10px' }}>Order</th>
                          <th style={{ padding: '6px 10px' }}>Change</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditLogs.map((log) => (
                          <tr key={log._id} style={{ borderBottom: '1px solid #f2f2f2' }}>
                            <td style={{ padding: '6px 10px', color: '#666', whiteSpace: 'nowrap' }}>
                              {log.createdAt ? formatDateTimeIST(log.createdAt) : '—'}
                            </td>
                            <td style={{ padding: '6px 10px' }}>
                              {log.actorUserId?.name || log.metadata?.actorName || 'Support'}
                              {log.actorUserId?.phone ? <span style={{ color: '#999', fontSize: 11 }}> · {log.actorUserId.phone}</span> : null}
                            </td>
                            <td style={{ padding: '6px 10px', fontWeight: 600 }}>
                              {log.metadata?.orderNumber || String(log.targetId || '').slice(-6)}
                              {log.metadata?.restaurantName ? <span style={{ color: '#999', fontSize: 11, fontWeight: 400 }}> · {log.metadata.restaurantName}</span> : null}
                            </td>
                            <td style={{ padding: '6px 10px' }}>
                              <span style={{ color: '#888' }}>{(log.before?.status || '').replace(/_/g, ' ')}</span>
                              <span style={{ margin: '0 6px' }}>→</span>
                              <span style={{ fontWeight: 600, color: '#c0392b' }}>{(log.after?.status || '').replace(/_/g, ' ')}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Filter */}
        <div className="card fade-in" style={{ marginBottom: 'var(--space-xl)' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-lg)', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ marginBottom: 0, flex: '1 1 220px' }}>
              <label style={{ fontWeight: '700', color: 'var(--gray-900)' }}>🔍 Filter by Status</label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                style={{ cursor: 'pointer' }}
              >
                <option value="">All Orders</option>
                <option value="pending">Pending</option>
                <option value="restaurant_confirmed">Restaurant Confirmed</option>
                <option value="preparing">Preparing</option>
                <option value="ready_for_pickup">Ready for Pickup</option>
                <option value="assigned_delivery">Assigned to Delivery</option>
                <option value="picked_up">Picked Up</option>
                <option value="on_the_way">On the Way</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0, flex: '1 1 200px' }}>
              <label style={{ fontWeight: '700', color: 'var(--gray-900)' }}>📦 Order Type</label>
              <select value={orderType} onChange={(e) => setOrderType(e.target.value)} style={{ cursor: 'pointer' }}>
                <option value="">All Types</option>
                <option value="food">🍽️ Food orders</option>
                <option value="delivery_service">🚚 Delivery service</option>
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0, flex: '0 1 170px' }}>
              <label style={{ fontWeight: '700', color: 'var(--gray-900)' }}>📅 From</label>
              <input
                type="date"
                value={startDate}
                max={endDate || undefined}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0, flex: '0 1 170px' }}>
              <label style={{ fontWeight: '700', color: 'var(--gray-900)' }}>📅 To</label>
              <input
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            {(startDate || endDate || filter) && (
              <button
                className="btn btn-secondary"
                onClick={() => { setStartDate(''); setEndDate(''); setFilter(''); }}
                style={{ marginBottom: 0 }}
              >
                ✕ Clear
              </button>
            )}
          </div>
          <div style={{ marginTop: 'var(--space-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)' }}>
            Dates use IST. Set only “From” for a single day (it filters that day), or both for a range. {(startDate || endDate) ? `Showing up to 500 orders in range.` : ''}
          </div>
        </div>

        {orders.length === 0 ? (
          <div className="card fade-in" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
            <div style={{ fontSize: '120px', marginBottom: 'var(--space-lg)' }}>📦</div>
            <h2 style={{
              fontSize: 'var(--font-size-3xl)',
              fontWeight: '700',
              color: 'var(--gray-700)',
              marginBottom: 'var(--space-md)'
            }}>
              No orders found
            </h2>
            <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-lg)' }}>
              {filter ? 'Try changing the filter' : 'Orders will appear here when customers place them'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 'var(--space-xl)' }}>
            {orders.filter(o => o.adminApproval?.status === 'pending').length > 0 && (
              <div style={{
                padding: 'var(--space-md) var(--space-lg)', background: 'rgba(255, 184, 77, 0.15)',
                border: '2px solid var(--warning, #ffb84d)', borderRadius: 10, fontWeight: 700, color: '#8a5a00'
              }}>
                ⏳ {orders.filter(o => o.adminApproval?.status === 'pending').length} order(s) awaiting your approval (COD &amp; delivery-service) — review and Approve/Reject below.
              </div>
            )}
            {orders.map(order => (
              <div key={order._id} className="card fade-in" style={{
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Status Badge */}
                <div style={{
                  position: 'absolute',
                  top: 'var(--space-lg)',
                  right: 'var(--space-lg)',
                  padding: 'var(--space-sm) var(--space-lg)',
                  borderRadius: 'var(--radius-full)',
                  background: getStatusColor(order.status),
                  color: 'white',
                  fontWeight: '700',
                  fontSize: 'var(--font-size-xs)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-xs)',
                  boxShadow: 'var(--shadow-md)'
                }}>
                  <span>{getStatusIcon(order.status)}</span>
                  <span>{order.status.replace(/_/g, ' ')}</span>
                </div>

                {/* Order Header */}
                <div style={{ paddingRight: '200px', marginBottom: 'var(--space-lg)' }}>
                  <h2 style={{
                    fontSize: 'var(--font-size-3xl)',
                    fontWeight: '800',
                    color: 'var(--gray-900)',
                    marginBottom: 'var(--space-xs)'
                  }}>
                    Order #{order.orderNumber}
                  </h2>
                  <p style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--gray-600)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-xs)'
                  }}>
                    <span>🕒</span>
                    <span>{formatDateTimeIST(order.createdAt)}</span>
                  </p>
                  <button
                    onClick={() => openOrderDetail(order)}
                    style={{
                      marginTop: 'var(--space-sm)',
                      padding: '8px 16px',
                      borderRadius: 'var(--radius-md, 10px)',
                      border: 'none',
                      background: 'var(--gradient-primary, #2aaa8a)',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 'var(--font-size-sm, 14px)',
                      cursor: 'pointer'
                    }}
                  >
                    🔍 Show Detail
                  </button>
                </div>

                {/* Delivery Service (courier-only) banner */}
                {order.orderType === 'delivery_service' && (
                  <div style={{
                    background: 'linear-gradient(135deg,#FFF7ED,#FFE3B3)',
                    border: '1px solid #F3B24B', borderRadius: 12,
                    padding: 'var(--space-lg)', marginBottom: 'var(--space-lg)'
                  }}>
                    <div style={{ fontWeight: 800, color: '#7A4A00', marginBottom: 6 }}>
                      🚚 Delivery Service {order.deliveryService?.paymentMode === 'prepaid' ? '· Prepaid' : '· COD'}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 8, fontSize: 13, color: '#5b4a2e' }}>
                      <div><b>Pickup phone:</b> {order.deliveryService?.pickupPhone || '—'}</div>
                      <div><b>Recipient:</b> {order.deliveryAddress?.name || '—'} · {order.deliveryAddress?.phone || '—'}</div>
                      <div><b>Drop:</b> {order.deliveryAddress?.street || (order.deliveryAddress?.coordinates ? `${order.deliveryAddress.coordinates.lat?.toFixed?.(5)}, ${order.deliveryAddress.coordinates.lng?.toFixed?.(5)}` : '—')}</div>
                      <div><b>Distance:</b> {order.deliveryService?.distanceKm != null ? `${order.deliveryService.distanceKm} km` : '—'}</div>
                      <div><b>Delivery charge:</b> ₹{((order.deliveryService?.chargePaisa || 0) / 100).toFixed(0)} <span style={{ color: '#9A6A00' }}>(platform earning)</span></div>
                    </div>
                  </div>
                )}

                {/* Order Details */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: 'var(--space-md)',
                  marginBottom: 'var(--space-lg)',
                  paddingBottom: 'var(--space-lg)',
                  borderBottom: '2px solid var(--gray-100)'
                }}>
                  <div style={{
                    padding: 'var(--space-md)',
                    background: 'rgba(78, 205, 196, 0.1)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid rgba(78, 205, 196, 0.2)'
                  }}>
                    <p style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--gray-600)',
                      marginBottom: 'var(--space-xs)',
                      fontWeight: '600'
                    }}>
                      CUSTOMER
                    </p>
                    <p style={{
                      fontSize: 'var(--font-size-base)',
                      fontWeight: '700',
                      color: 'var(--secondary)',
                      margin: 0
                    }}>
                      👤 {order.userId?.name || 'Unknown User'}
                    </p>
                    <p style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--gray-600)',
                      margin: 0
                    }}>
                      {order.userId?.email || 'N/A'}
                    </p>
                    {order.userId && typeof order.customerTotalOrders === 'number' && (
                      <span style={{
                        display: 'inline-block',
                        marginTop: 6,
                        padding: '2px 9px',
                        borderRadius: 12,
                        fontSize: 11,
                        fontWeight: 700,
                        background: order.customerTotalOrders <= 1 ? 'rgba(34,197,94,0.15)' : 'rgba(79,70,229,0.12)',
                        color: order.customerTotalOrders <= 1 ? '#15803d' : '#4338ca'
                      }}>
                        {order.customerTotalOrders <= 1
                          ? '🆕 New customer'
                          : `🛍️ ${order.customerTotalOrders} total orders`}
                      </span>
                    )}
                  </div>

                  <div style={{
                    padding: 'var(--space-md)',
                    background: 'rgba(255, 107, 107, 0.1)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid rgba(255, 107, 107, 0.2)'
                  }}>
                    <p style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--gray-600)',
                      marginBottom: 'var(--space-xs)',
                      fontWeight: '600'
                    }}>
                      RESTAURANT
                    </p>
                    <p style={{
                      fontSize: 'var(--font-size-base)',
                      fontWeight: '700',
                      color: 'var(--primary)',
                      margin: 0
                    }}>
                      🏪 {order.restaurantId?.name || 'Unknown Restaurant'}
                    </p>
                    <p style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--gray-600)',
                      marginTop: 'var(--space-xs)',
                      margin: 0
                    }}>
                      📱 {order.restaurantId?.phone || 'N/A'}
                    </p>
                  </div>

                  {order.deliveryPartnerId && (
                    <div style={{
                      padding: 'var(--space-md)',
                      background: 'rgba(255, 184, 77, 0.1)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid rgba(255, 184, 77, 0.2)'
                    }}>
                      <p style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--gray-600)',
                        marginBottom: 'var(--space-xs)',
                        fontWeight: '600'
                      }}>
                        DELIVERY PARTNER
                      </p>
                      <p style={{
                        fontSize: 'var(--font-size-base)',
                        fontWeight: '700',
                        color: 'var(--warning)',
                        margin: 0
                      }}>
                        🚴 {order.deliveryPartnerId?.name || 'Unknown Partner'}
                      </p>
                      <p style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--gray-600)',
                        marginTop: 'var(--space-xs)',
                        margin: 0
                      }}>
                        📱 {order.deliveryPartnerId?.phone || 'N/A'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Payment & Total */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: 'var(--space-md)'
                }}>
                  <div style={{
                    padding: 'var(--space-md)',
                    background: 'var(--gray-50)',
                    borderRadius: 'var(--radius-md)',
                    textAlign: 'center'
                  }}>
                    <p style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--gray-600)',
                      marginBottom: 'var(--space-xs)',
                      fontWeight: '600'
                    }}>
                      ORDER TOTAL
                    </p>
                    <p style={{
                      fontSize: 'var(--font-size-2xl)',
                      fontWeight: '800',
                      color: 'var(--primary)',
                      margin: 0
                    }}>
                      ₹{order.pricing.total}
                    </p>
                  </div>

                  <div style={{
                    padding: 'var(--space-md)',
                    background: 'var(--gray-50)',
                    borderRadius: 'var(--radius-md)',
                    textAlign: 'center'
                  }}>
                    <p style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--gray-600)',
                      marginBottom: 'var(--space-xs)',
                      fontWeight: '600'
                    }}>
                      PAYMENT
                    </p>
                    <p style={{
                      fontSize: 'var(--font-size-base)',
                      fontWeight: '700',
                      color: 'var(--gray-900)',
                      margin: 0,
                      textTransform: 'uppercase'
                    }}>
                      💳 {order.paymentMethod}
                    </p>
                    <p style={{
                      fontSize: 'var(--font-size-xs)',
                      color: order.paymentStatus === 'paid' ? 'var(--success)' : 'var(--warning)',
                      margin: 0,
                      textTransform: 'uppercase'
                    }}>
                      {order.paymentStatus}
                    </p>
                  </div>
                </div>

                {/* Status Update Section */}
                {order.status !== 'delivered' && order.status !== 'cancelled' && (
                  <div style={{
                    marginTop: 'var(--space-lg)',
                    paddingTop: 'var(--space-lg)',
                    borderTop: '2px solid var(--gray-100)'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-md)',
                      flexWrap: 'wrap'
                    }}>
                      <label style={{
                        fontWeight: '700',
                        color: 'var(--gray-700)',
                        fontSize: 'var(--font-size-sm)'
                      }}>
                        🔄 Update Status:
                      </label>
                      <select
                        value={order.status}
                        onChange={(e) => handleStatusChange(order._id, e.target.value)}
                        disabled={updatingOrder === order._id}
                        style={{
                          padding: 'var(--space-sm) var(--space-md)',
                          borderRadius: 'var(--radius-md)',
                          border: '2px solid var(--gray-200)',
                          background: 'white',
                          fontWeight: '600',
                          fontSize: 'var(--font-size-sm)',
                          cursor: updatingOrder === order._id ? 'wait' : 'pointer',
                          minWidth: '200px',
                          opacity: updatingOrder === order._id ? 0.6 : 1
                        }}
                      >
                        {orderStatuses.map(status => (
                          <option key={status} value={status}>
                            {getStatusIcon(status)} {status.replace(/_/g, ' ').toUpperCase()}
                          </option>
                        ))}
                      </select>
                      {updatingOrder === order._id && (
                        <span style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-sm)' }}>
                          ⏳ Updating...
                        </span>
                      )}
                      {order.status === 'assigned_delivery' && (
                        <span style={{
                          background: 'rgba(81, 207, 102, 0.1)',
                          color: 'var(--success)',
                          padding: 'var(--space-xs) var(--space-sm)',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: '600'
                        }}>
                          💡 Change to "delivered" to generate invoices
                        </span>
                      )}
                    </div>

                    {/* COD admin-approval gate */}
                    {order.adminApproval?.status === 'pending' && (
                      <div style={{
                        marginTop: 'var(--space-md)', padding: 'var(--space-md)',
                        background: 'rgba(255, 184, 77, 0.12)', border: '1px solid var(--warning, #ffb84d)',
                        borderRadius: 8, display: 'flex', alignItems: 'center', gap: 'var(--space-md)', flexWrap: 'wrap'
                      }}>
                        <span style={{ fontWeight: 700, color: '#b8860b' }}>⏳ Awaiting your approval ({order.orderType === 'delivery_service' ? 'Delivery Service' : 'COD'})</span>
                        <button
                          onClick={() => handleApproveOrder(order._id, order.orderNumber)}
                          disabled={processingApproval === order._id}
                          style={{ padding: '7px 18px', background: 'var(--success, #2aaa8a)', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: processingApproval === order._id ? 'wait' : 'pointer' }}
                        >
                          {processingApproval === order._id ? '…' : '✓ Approve & send to restaurant'}
                        </button>
                        <button
                          onClick={() => handleRejectOrder(order._id, order.orderNumber)}
                          disabled={processingApproval === order._id}
                          style={{ padding: '7px 18px', background: 'var(--danger, #e74c3c)', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: processingApproval === order._id ? 'wait' : 'pointer' }}
                        >
                          ✕ Reject
                        </button>
                      </div>
                    )}

                    {/* (Re)trigger delivery — for 'preparing' / 'ready_for_pickup' (dispatch) + 'assigned_delivery' (reassign) food orders */}
                    {['preparing', 'ready_for_pickup', 'assigned_delivery'].includes(order.status) && order.orderType !== 'delivery_service' && order.orderType !== 'logistics' && (
                      <div style={{
                        marginTop: 'var(--space-md)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-md)',
                        flexWrap: 'wrap'
                      }}>
                        <button
                          onClick={() => handleRetriggerDelivery(order._id, order.orderNumber, order.status)}
                          disabled={retriggeringOrder === order._id}
                          style={{
                            padding: 'var(--space-sm) var(--space-lg)',
                            borderRadius: 'var(--radius-md)',
                            border: 'none',
                            background: retriggeringOrder === order._id
                              ? 'var(--gray-300)'
                              : 'linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%)',
                            color: 'white',
                            fontWeight: '700',
                            fontSize: 'var(--font-size-sm)',
                            cursor: retriggeringOrder === order._id ? 'wait' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-xs)',
                            boxShadow: 'var(--shadow-sm)',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {retriggeringOrder === order._id ? (
                            <>⏳ Re-triggering...</>
                          ) : (
                            <>🔄 Re-trigger Delivery Assignment</>
                          )}
                        </button>
                        <span style={{
                          background: 'rgba(255, 107, 107, 0.1)',
                          color: 'var(--primary)',
                          padding: 'var(--space-xs) var(--space-sm)',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: '600'
                        }}>
                          ⚠️ Use if delivery partner needs to be changed
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Hover Accent */}
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '200px',
                  height: '200px',
                  background: 'radial-gradient(circle at bottom left, rgba(78, 205, 196, 0.1) 0%, transparent 70%)',
                  pointerEvents: 'none'
                }} />
              </div>
            ))}
          </div>
        )}
      </div>

      {detailOrder && (() => {
        const o = detailOrder;
        const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 14px', zIndex: 1000, overflowY: 'auto' };
        const box = { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 840, boxShadow: '0 20px 60px rgba(0,0,0,0.35)', overflow: 'hidden', marginBottom: 40 };
        const sectionCard = { background: 'var(--gray-50, #f7f8fa)', border: '1px solid var(--gray-100, #f3f4f6)', borderRadius: 12, padding: 14, marginBottom: 12 };
        const sectionTitle = { fontWeight: 800, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--primary, #2aaa8a)', marginBottom: 10 };
        const skip = ['__v', '__partial'];
        // When the "raw" toggle is off, hide the ₹-paisa integer duplicates (e.g. totalPaisa) that
        // mirror the rupee amounts, for a cleaner read. Default ON shows every field.
        const hide = showRawFields ? null : (k) => /Paisa$/.test(k);
        const visible = (k) => !skip.includes(k) && !(hide && hide(k));
        const scalarKeys = Object.keys(o).filter((k) => visible(k) && (o[k] === null || typeof o[k] !== 'object'));
        const objectKeys = Object.keys(o).filter((k) => visible(k) && o[k] !== null && typeof o[k] === 'object');
        const sumItem = (label, val) => (
          <div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>{label}</div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{val}</div>
          </div>
        );
        const cust = o.userId || o.userInfo || {};
        const rest = o.restaurantId || {};
        const dp = o.deliveryPartnerId;
        const addr = o.deliveryAddress || {};
        const addrStr = [addr.street, addr.area, addr.landmark, addr.city, addr.state, addr.zipCode]
          .filter(Boolean).join(', ');
        const total = (o.pricing && (o.pricing.grandTotal ?? o.pricing.total)) ?? o.totalAmount;
        return (
          <div style={overlay} onClick={() => setDetailOrder(null)}>
            <div style={box} onClick={(e) => e.stopPropagation()}>
              {/* Header + summary */}
              <div style={{ background: 'var(--gradient-primary, linear-gradient(135deg, #2aaa8a, #1f8a70))', color: '#fff', padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: 22, fontWeight: 800 }}>Order #{o.orderNumber}</div>
                    <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2 }}>{detailLoading ? 'Loading full detail…' : (o.__partial ? '' : 'Complete order detail')}</div>
                  </div>
                  <button onClick={() => setDetailOrder(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', width: 34, height: 34, borderRadius: 8, fontSize: 18, cursor: 'pointer' }}>✕</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginTop: 16 }}>
                  {sumItem('Status', (o.status || '—').replace(/_/g, ' '))}
                  {sumItem('Customer', `${cust.name || '—'}${cust.phone ? ' · ' + cust.phone : ''}`)}
                  {sumItem('Delivery Address', addrStr || (detailLoading ? 'Loading…' : '—'))}
                  {o.specialInstructions && sumItem('📝 Instructions', o.specialInstructions)}
                  {sumItem('Restaurant', rest.name || '—')}
                  {sumItem('Delivery Partner', dp ? `${dp.name || '—'}${dp.phone ? ' · ' + dp.phone : ''}` : 'Not assigned')}
                  {sumItem('Payment', `${o.paymentMethod || '—'} / ${o.paymentStatus || '—'}`)}
                  {sumItem('Total', total != null ? `₹${total}` : '—')}
                  {sumItem('Placed', o.createdAt ? formatDateTimeIST(o.createdAt) : '—')}
                  {o.deliveredAt && sumItem('Delivered', formatDateTimeIST(o.deliveredAt))}
                </div>
              </div>

              {/* Full detail */}
              <div style={{ padding: 20, maxHeight: '62vh', overflowY: 'auto' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--gray-600, #4b5563)', marginBottom: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={showRawFields} onChange={(e) => setShowRawFields(e.target.checked)} />
                  Show ₹-paisa &amp; technical fields
                </label>

                {/* Zomato / Swiggy price comparison (admin only, on-demand) */}
                {user?.role !== 'support' && (
                  <div style={sectionCard}>
                    <div style={sectionTitle}>Zomato / Swiggy price comparison</div>
                    {!compCost ? (
                      <div>
                        <button onClick={() => loadCompetitorCost(o._id)} disabled={compLoading}
                          style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: compLoading ? 'default' : 'pointer', opacity: compLoading ? 0.7 : 1 }}>
                          {compLoading ? 'Matching items to competitor menus…' : '🍽️ Compare on Zomato / Swiggy'}
                        </button>
                        {compErr && <div style={{ color: '#dc2626', fontSize: 12.5, marginTop: 8 }}>{compErr}</div>}
                        <div style={{ fontSize: 11.5, color: 'var(--gray-500,#6b7280)', marginTop: 8 }}>
                          Menu-price comparison from the last Competitor Intel scrape — <b>item prices only</b>, not the platform's final bill (excludes their delivery / packaging / platform fees &amp; app discounts).
                        </div>
                      </div>
                    ) : (
                      <CompetitorCostPanel data={compCost} />
                    )}
                  </div>
                )}

                <div style={sectionCard}>
                  <div style={sectionTitle}>Order</div>
                  <DetailValue value={scalarKeys.reduce((acc, k) => { acc[k] = o[k]; return acc; }, {})} hide={hide} />
                </div>
                {objectKeys.map((k) => (
                  <div key={k} style={sectionCard}>
                    <div style={sectionTitle}>{humanizeKey(k)}</div>
                    <DetailValue value={o[k]} hide={hide} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default AdminOrders;
